/**
 * Firestore Collection Name Constants
 *
 * This file defines all collection names used in the application.
 * Using constants ensures consistency and enables TypeScript to catch typos.
 *
 * Collections are organized by data source:
 * - DataHub AppSheet collections (synced from AppSheet)
 * - DataHub QBO collections (synced from QuickBooks Online)
 * - Local augmentation collections (app-specific overrides)
 * - Core application collections
 */

export const COLLECTIONS = {
  // ===========================================
  // DataHub AppSheet Collections (read-only sync)
  // ===========================================

  /** Generator/equipment data synced from AppSheet Service App */
  APPSHEET_GENERATORS: 'appsheet_generators',

  /** Parts list/inventory data synced from AppSheet */
  APPSHEET_PARTS_LIST: 'appsheet_parts_list',

  /** Field service logs synced from AppSheet */
  APPSHEET_SERVICE_LOGS: 'appsheet_service_logs',

  /** Shared contacts synced from AppSheet */
  APPSHEET_SHARED_CONTACTS: 'appsheet_shared_contacts',

  // APPSHEET_INSTALL_LOGS removed — Install App retired, migrated to native projects

  /** Schedule blocks synced from AppSheet Service App */
  APPSHEET_SCHEDULE_BLOCKS: 'appsheet_schedule_blocks',

  /** Service parts synced from AppSheet Service App */
  APPSHEET_SERVICE_PARTS: 'appsheet_service_parts',

  /** Service files/attachments synced from AppSheet Service App */
  APPSHEET_SERVICE_FILES: 'appsheet_service_files',

  /** Recurring service schedules synced from AppSheet Service App */
  APPSHEET_RECURRING_SCHEDULES: 'appsheet_recurring_schedules',

  // ===========================================
  // DataHub QBO Collections (read-only sync)
  // ===========================================

  /** Customers synced from QuickBooks Online */
  QBO_CUSTOMERS: 'qbo_customers',

  /** Invoices synced from QuickBooks Online */
  QBO_INVOICES: 'qbo_invoices',

  /** Payments synced from QuickBooks Online */
  QBO_PAYMENTS: 'qbo_payments',

  /** Items/Products synced from QuickBooks Online */
  QBO_ITEMS: 'qbo_items',

  /** Chart of Accounts synced from QuickBooks Online */
  QBO_ACCOUNTS: 'qbo_accounts',

  /** Time activities (billable hours) synced from QuickBooks Online */
  QBO_TIME_ACTIVITIES: 'qbo_time_activities',

  /** Employees synced from QuickBooks Online */
  QBO_EMPLOYEES: 'qbo_employees',

  /** Vendors synced from QuickBooks Online */
  QBO_VENDORS: 'qbo_vendors',

  /** Bills synced from QuickBooks Online */
  QBO_BILLS: 'qbo_bills',

  // ===========================================
  // Local Augmentation Collections
  // ===========================================

  /** Local price overrides for DataHub parts */
  PRICE_BOOK_OVERRIDES: 'priceBookOverrides',

  /** Price book items (legacy quoting system) */
  PRICE_BOOK_ITEMS: 'priceBookItems',

  /** Locally-created customers (not in QBO) */
  LOCAL_CUSTOMERS: 'local_customers',
  /** Do-not-service registry (Nick M117/M175). Read-only here; written by customers_setDoNotService. */
  DO_NOT_SERVICE: 'doNotService',
  /** Generator model-spec catalog (#4552). Read here; written only by crm_upsertGeneratorModelSpec. */
  GENERATOR_MODEL_SPECS: 'generatorModelSpecs',
  /** Proposal promos (#4552 S1). Read here; written only by crm_upsertProposalPromo. */
  PROPOSAL_PROMOS: 'proposalPromos',
  /** The org's standard warranty line (#4552 S1), doc id = org id. Written only by crm_setProposalStandardWarranty. */
  PROPOSAL_STANDARD_TERMS: 'proposalStandardTerms',

  /** Scheduling vendors (subcontractors a scheduler can assign). Read here; written only by projects_*Vendor callables. */
  VENDORS: 'vendors',

  /** Generator-level bill of materials — auto-populated from service parts, manager-curated (BOM feature, spec §8) */
  GENERATOR_BOM_ENTRIES: 'generatorBomEntries',

  /** Recurring schedule scope-of-work + BOM picks (BOM feature, spec §9) */
  RECURRING_SCHEDULE_EXTRAS: 'recurringScheduleExtras',
  PARTS_TEMPLATES: 'partsTemplates',
  RECURRING_PARTS_LOADS: 'recurringPartsLoads',
  FIELD_SERVICE_SETTINGS: 'fieldServiceSettings',

  /** Generator pricing card entries — manual and contract-derived pricing rows (BOM feature, spec §10) */
  GENERATOR_PRICING: 'generatorPricing',

  /** Generator photo library + curated gallery rows, top-level, org-scoped (gallery blueprint 2026-09-26 §2.1) */
  GENERATOR_PHOTOS: 'generatorPhotos',
  /** Drawings over a generator photo (gallery F-6). Allow-listed readers only: scripts/guards/check-markup-isolation.mjs. */
  GENERATOR_PHOTO_MARKUP: 'generatorPhotoMarkup',
  /** Verified per-visit AI Summary, one doc per Service ID (Service History Accuracy P6). Read-only for clients. */
  SERVICE_HISTORY_SUMMARIES: 'service_history_summaries',
  SERVICE_FOLLOWUP_PROPOSALS: 'service_followup_proposals',

  /** Fluid samples — one record per physical bottle (docs/field-service/fluid-samples/PRD.md §9) */
  FLUID_SAMPLES: 'fluidSamples',

  /** Fluid sample shipments — one package to one lab (PRD §9.4) */
  SAMPLE_SHIPMENTS: 'sampleShipments',

  /** Lab profiles — per-lab configuration and the sender trust boundary (PRD §7.4a) */
  LAB_PROFILES: 'labProfiles',

  /** Lab results — one per lab report attachment (PRD §7.5a); written from Phase 1 */
  LAB_RESULTS: 'labResults',

  /** Per-ticket FSR fluid state for sample reconciliation (PRD §9.2); server-internal */
  FLUID_SAMPLE_TICKET_STATE: 'fluidSampleTicketState',
  LAB_API_SYNC_STATE: 'labApiSyncState',

  // ===========================================
  // Core Application Collections
  // ===========================================

  /** Quotes created in the application */
  QUOTES: 'quotes',

  /** Quote templates - saved quote configurations for reuse */
  QUOTE_TEMPLATES: 'quoteTemplates',

  // Quoting Module — Project Data
  /** Price sheets with conduit/wire/gas/labor rates for project quotes */
  PROJECT_PRICE_SHEETS: 'projectPriceSheets',
  /** Generator models with specs and OPG dealer costs */
  GENERATOR_CATALOG: 'generatorCatalog',
  /** Automatic Transfer Switch models with specs and OPG costs */
  ATS_CATALOG: 'atsCatalog',

  /** Invoices created in the application */
  INVOICES: 'invoices',

  /** Draft invoices pending review and QBO push */
  INVOICE_DRAFTS: 'invoice_drafts',

  /** QuickBooks item mappings for invoicing */
  QBO_ITEM_MAPPINGS: 'qboItemMappings',

  /** Terms and conditions templates */
  TERMS_AND_CONDITIONS: 'termsAndConditions',

  // ===========================================
  // Projects Module (P10) — Native Collections
  // ===========================================

  /** Native projects — OfficeHub-owned install projects */
  PROJECTS: 'projects',

  /** Agency directory — shared across organization */
  AGENCIES: 'agencies',

  /** Native project equipment — top-level, linked via projectId */
  PROJECT_EQUIPMENT: 'projectEquipment',

  /** Install invoices — project billing records with optional QBO link */
  INSTALL_INVOICES: 'installInvoices',

  /** Project-to-QBO-invoice links — join table for billing rollup */
  PROJECT_INVOICE_LINKS: 'project_invoice_links',

  /** Invoice-service ticket links — explicit links between QBO invoices and service tickets */
  INVOICE_SERVICE_LINKS: 'invoice_service_links',

  /** Checklist templates — admin-editable master checklists */
  CHECKLIST_TEMPLATES: 'checklistTemplates',

  /** Project tasks — per-project task subcollection (projects/{id}/tasks) */
  PROJECT_TASKS: 'tasks',

  /** Project settings subcollection (under organizations/{orgId}/) */
  PROJECT_SETTINGS: 'projectSettings',

  /** Project change orders — per-project subcollection (projects/{id}/changeOrders) */
  CHANGE_ORDERS: 'changeOrders',

  /** Organizations (multi-tenant) */
  ORGANIZATIONS: 'organizations',

  /** User profiles */
  USERS: 'users',

  /** Per-org dismissals of "Needs Review" rows on the User System Linking admin page.
   *  Doc shape: { personId, organizationId, dismissedAt (Timestamp), dismissedBy, dismissedByEmail, reason? } */
  USER_LINKING_DISMISSALS: 'userLinkingDismissals',

  // ===========================================
  // Billing & Collections Module (P4)
  // ===========================================

  /** Customer collection profiles - invoicing preferences, contacts, payer behavior */
  CUSTOMER_COLLECTION_PROFILES: 'customerCollectionProfiles',

  /** Collection items - invoice-level collection tracking */
  COLLECTION_ITEMS: 'collectionItems',

  /** Collection events - activity log (emails, calls, status changes) */
  COLLECTION_EVENTS: 'collectionEvents',

  /** Collection rules - configurable automation rules */
  COLLECTION_RULES: 'collectionRules',

  /** Collection email templates */
  COLLECTION_EMAIL_TEMPLATES: 'collectionEmailTemplates',


  /** Collection snapshots - daily/weekly/monthly AR state for dashboard trends */
  COLLECTION_SNAPSHOTS: 'collectionSnapshots',

  /** Collection alerts - configurable dashboard notifications */
  COLLECTION_ALERTS: 'collectionAlerts',

  /** Collection alert dismissals - per-user dismissal records */
  COLLECTION_ALERT_DISMISSALS: 'collectionAlertDismissals',

  /** Collection alert rules - user-configurable alert triggers */
  COLLECTION_ALERT_RULES: 'collectionAlertRules',

  /** CC payment settings - per-realm surcharge config */
  CC_PAYMENT_SETTINGS: 'cc_payment_settings',

  /** CC payment audit log - tracks surcharge additions */
  CC_PAYMENT_AUDIT: 'cc_payment_audit',

  /** CC payment locks - concurrency control for invoice updates */
  CC_PAYMENT_LOCKS: 'cc_payment_locks',

  /** Collections dashboard UI state (per-user) */
  COLLECTIONS_DASHBOARD_STATE: 'collectionsDashboardState',

  /** Collections work queue UI state (per-user) */
  COLLECTIONS_WORK_QUEUE_STATE: 'collectionsWorkQueueState',

  /** Collections sync status */
  COLLECTIONS_SYNC_STATUS: 'collectionsSyncStatus',

  /** Collection settings - per-org automation rule configuration */
  COLLECTION_SETTINGS: 'collectionSettings',

  /** Automation settings - per-org automation feature toggles */
  AUTOMATION_SETTINGS: 'automationSettings',

  /** Stripe/QBO payment links */
  PAYMENT_LINKS: 'payment_links',

  /** QBO invoice attachments */
  QBO_ATTACHMENTS: 'qbo_attachments',

  // ===========================================
  // Fleet Management Module (P7)
  // ===========================================

  /** VIN exceptions - allows marking non-standard VINs as valid */
  VIN_EXCEPTIONS: 'vinExceptions',

  /** Fleet action items - signal-detected issues requiring attention */
  FLEET_ACTION_ITEMS: 'fleet_action_items',

  /** Fleet vehicle inspections */
  FLEET_INSPECTIONS: 'fleet_inspections',

  /** Fleet tasks - maintenance and repair work items */
  FLEET_TASKS: 'fleet_tasks',

  /** Fleet settings subcollection (under organizations/{orgId}/) */
  FLEET_SETTINGS: 'fleetSettings',

  /** Inspection requests sent to drivers */
  INSPECTION_REQUESTS: 'inspection_requests',

  // ===========================================
  // CRM Module (P3)
  // ===========================================

  /** Sales leads and prospects */
  LEADS: 'leads',

  /** Restricted-pipeline deals (e.g. M&A): a locked store, readable only by the pipeline's list */
  RESTRICTED_LEADS: 'restricted_leads',

  /** Per-user lead pins ("Watching" list). Doc ID is `${userId}_${leadId}`. */
  LEAD_PINS: 'leadPins',

  /** Shared contacts */
  CONTACTS: 'contacts',

  /** Sales pipelines configuration */
  PIPELINES: 'pipelines',

  /** AI Sales Chat conversations */
  CONVERSATIONS: 'conversations',

  /** AI-generated email sequences */
  EMAIL_SEQUENCES: 'emailSequences',

  /** Email sequence enrollments - leads enrolled in sequences */
  SEQUENCE_ENROLLMENTS: 'sequenceEnrollments',

  /** Credit spend audit log for Apollo.io operations */
  CREDIT_SPEND_LOG: 'creditSpendLog',

  // ===========================================
  // Support Module (P6)
  // ===========================================

  /** Support cases - customer support tracking */
  SUPPORT_CASES: 'supportCases',

  /** Shared account credentials — vendor, dealer, permitting, utility logins. Callable-only access (server-encrypted passwords). */
  SUPPORT_LOGINS: 'supportLogins',

  /** Support brief subcollection name — used as organizations/{orgId}/supportBriefs/latest */
  SUPPORT_BRIEFS: 'supportBriefs',

  /** Call logs - phone call records */
  CALL_LOGS: 'callLogs',

  /** Support settings subcollection — organizations/{orgId}/supportSettings/ */
  SUPPORT_SETTINGS: 'supportSettings',

  /**
   * Support on-call rota subcollection — organizations/{orgId}/onCallRota/{weekStart}_{branchId}.
   * One doc per (Monday, branch). Client-readable; written only by the server (support_dispatch,
   * support_onCallSendReminder and the Monday job support_onCallWeeklyReminder).
   */
  ON_CALL_ROTA: 'onCallRota',

  /** Raw inbound payloads from the Startel call center External POST integration. Source-of-truth for replay. */
  STARTEL_INBOUND_LOG: 'startelInboundLog',

  /** SMS thread metadata — organizations/{orgId}/smsThreads/{phone10} */
  SMS_THREADS: 'smsThreads',

  /** Service tickets - field service work orders */
  SERVICE_TICKETS: 'serviceTickets',

  /**
   * Immutable history notes attached to a service ticket, written ONLY by
   * `fieldService_addTicketHistoryNote`. Subcollection of SERVICE_TICKETS.
   */
  SERVICE_TICKET_HISTORY_NOTES: 'historyNotes',

  /**
   * Ticket Owners grid — one doc per branch x stage cell (default owner for confirmed
   * handoffs). Read through `fieldService_getOwnerGrid`; written only by the manager-gated
   * callables. Mirrors OWNER_GRID_COLLECTIONS in functions/src/config/collections.ts.
   */
  FIELD_SERVICE_OWNER_GRID: 'fieldServiceOwnerGrid',
  /** Append-only history of Ticket Owners grid changes. Server-only (no client access). */
  FIELD_SERVICE_OWNER_GRID_HISTORY: 'fieldServiceOwnerGridHistory',

  /** Physical service sites/locations */
  SITES: 'sites',

  /** Site survey sessions - AI-guided site assessments linked to leads */
  SITE_SURVEYS: 'siteSurveys',

  // ===========================================
  // AI Interviewing Module
  // ===========================================

  /** Interview sessions - AI-powered interviews (reference checks, feedback, etc.) */
  INTERVIEW_SESSIONS: 'interviewSessions',

  /** Interview scripts - configuration for different interview types */
  INTERVIEW_SCRIPTS: 'interviewScripts',

  // ===========================================
  // Forms & Documents Module
  // ===========================================

  /** Form templates - configurable form definitions */
  FORM_TEMPLATES: 'formTemplates',

  /** Form submissions - user-submitted form data */
  FORM_SUBMISSIONS: 'formSubmissions',

  /**
   * Ticket report flags (organizations/{orgId}/ticketReportFlags/{serviceId}) -
   * one doc per service ticket that currently has an UNFILED field report, none
   * for any other ticket. Server-computed (functions/src/forms/ticketReportFlag.ts),
   * read-only here. Drives the non-blocking "report not submitted" warning.
   */
  TICKET_REPORT_FLAGS: 'ticketReportFlags',

  /**
   * GoFormz CSV archive (top-level, org-scoped) - one doc per GoFormz FormId
   * holding that form's exported field data VERBATIM under its real GoFormz
   * column name. READ-ONLY: legacy reports are not editable, so a user's note
   * goes on the TICKET, never in here. The GoFormz viewer shows this BENEATH
   * the archived PDF - PDF first, structured data second.
   *
   * Read the whole document, never a dot-path into `fields`: a GoFormz column
   * name may contain a '.' (e.g. "V.A.C"), which makes field-path access
   * resolve to the wrong place.
   */
  GOFORMZ_FORM_DATA: 'goformz_form_data',
  // Photo index for the archived GoFormz forms — one doc per image,
  // id `${goformzFormId}_${fileId}`. The JOIN to a form's photos; the Storage
  // path carries no org, ticket or generator segment by ruling.
  GOFORMZ_FORM_IMAGES: 'goformz_form_images',

  /**
   * Form assignments (top-level, org-scoped) — manual ticket->template join
   * (Forms Platform Phase 1c). Server-written only; client-readable via
   * org-scoped rules. `assignmentSource` is the seam for rule-based
   * auto-assign (#2161).
   */
  FORM_ASSIGNMENTS: 'formAssignments',

  /** FSR error log events — structured debug instrumentation, Admin/Manager read only */
  FORM_ERROR_LOGS: 'formErrorLogs',

  // ===========================================
  // Service Contract Execution (Field Service · P11)
  // ===========================================
  // 8-collection contract model (PRD #2972). Server-write-only, org-scoped read.
  // Not P3 — contracts live under Field Service (P11).

  /** Core contract records (idempotent internal contractId, Drive folder, term) */
  CONTRACTS: 'contracts',

  /** Equipment covered by a contract (effective-dated slices, Decision 14) */
  CONTRACT_EQUIPMENT: 'contractEquipment',

  /** Service definitions per contract, one per ServiceOperation (effective-dated) */
  CONTRACT_SERVICES: 'contractServices',

  /** Service locations per contract */
  CONTRACT_LOCATIONS: 'contractLocations',

  /** Contact assignments per contract */
  CONTRACT_CONTACTS: 'contractContacts',

  /** Cadence intent per (equipment x service) — assist, no hard auto-generation */
  CONTRACT_SCHEDULES: 'contractSchedules',

  /** Uploaded contract documents (document set for AI ingestion) */
  CONTRACT_DOCUMENTS: 'contractDocuments',

  /** AI multi-document ingestion sessions (resumable wizard) */
  CONTRACT_INGESTION_SESSIONS: 'contractIngestionSessions',
  /** Contract setup worklist lines — one per unit / service / schedule / term (setup worklist spec 2026-10-02) */
  CONTRACT_SETUP_ITEMS: 'contractSetupItems',
  /** Server-only lock naming a unit's OPEN contractEquipment slice (one per contract x unit). Never read by the client. */
  CONTRACT_EQUIPMENT_OPEN_LOCKS: 'contractEquipmentOpenLocks',
  /** Office Cheat Sheet per contract, keyed by contract id (setup worklist PR 5) */
  CONTRACT_CHEAT_SHEETS: 'contractCheatSheets',

  /** Document templates - PDF template definitions for Smart Document Builder */
  DOCUMENT_TEMPLATES: 'documentTemplates',

  /** Document template versions - version history for templates */
  DOCUMENT_TEMPLATE_VERSIONS: 'documentTemplateVersions',

  // ===========================================
  // Unified Email System
  // ===========================================

  /** Email drafts - auto-saved email compositions */
  EMAIL_DRAFTS: 'emailDrafts',

  /** Scheduled emails - emails scheduled for future send */
  EMAIL_SCHEDULED: 'emailScheduled',

  /** Snoozed emails - emails snoozed with reminder timestamps */
  EMAIL_SNOOZED: 'emailSnoozed',

  /** Archived emails - archived email references */
  EMAIL_ARCHIVED: 'emailArchived',

  /** Unified email templates - reusable templates (global, module, user scopes) */
  EMAIL_TEMPLATES_UNIFIED: 'emailTemplatesUnified',

  /** AI email polish audit log - before/after pairs written by polishRenderedEmail */
  EMAIL_FINAL_PASS_LOG: 'emailFinalPassLog',

  // ===========================================
  // Gmail Integration
  // ===========================================

  /** Gmail sync queue - webhook notifications waiting to be processed */
  GMAIL_SYNC_QUEUE: 'gmail_sync_queue',

  /** Email messages - synced emails from Gmail */
  EMAIL_MESSAGES: 'emailMessages',

  /** Email sync state - tracks sync progress per user */
  EMAIL_SYNC_STATE: 'emailSyncState',

  /** Record email links - index for querying emails by record */
  RECORD_EMAIL_LINKS: 'recordEmailLinks',

  // ===========================================
  // Voice & Voicemail
  // ===========================================

  /** Voicemail templates - reusable VM scripts/recordings */
  VOICEMAIL_TEMPLATES: 'voicemailTemplates',

  /** Voicemail drops - individual drop delivery records */
  VOICEMAIL_DROPS: 'voicemailDrops',

  /** Voicemail campaigns - bulk campaign tracking */
  VOICEMAIL_CAMPAIGNS: 'voicemailCampaigns',

  /** Do Not Call list entries */
  DNC_LIST: 'dncList',

  // ===========================================
  // Dialpad Integration
  // ===========================================

  /** Live call context docs written by Dialpad webhook, auto-expire in 4h */
  LIVE_CALLS: 'liveCalls',

  // ===========================================
  // Universal Asset Management
  // ===========================================

  /** Admin-defined asset categories with field/compliance config */
  ASSET_CATEGORIES: 'assetCategories',

  /** Universal asset records (replaces appsheet_company_assets) */
  ASSETS: 'assets',

  /** Activity log subcollection under assets/{id}/activity */
  ASSET_ACTIVITY: 'activity',

  /** Alert rate-limiting subcollection under assets/{id}/alertLog */
  ASSET_ALERT_LOG: 'alertLog',

  // ─── Knowledge Base & AI ────────────────────────────────────────────────────

  /** Assets Agent conversation threads — one doc per session, turns in subcollection */
  ASSETS_AGENT_CONVERSATIONS: 'assets_agent_conversations',

  /** AI-generated knowledge documents from structured data ingestion */
  KNOWLEDGE_BASE: 'knowledge_base',

  /** AI discovery sessions — data analysis sessions */
  DISCOVERY_SESSIONS: 'discovery_sessions',

  /** AI discovery insights — patterns and anomalies from analysis */
  DISCOVERY_INSIGHTS: 'discovery_insights',

  /** AI landscape reports — comprehensive data analysis reports */
  LANDSCAPE_REPORTS: 'landscape_reports',

  // ─── Technician Module (P12) ────────────────────────────────────────────────

  /** AI-generated pre-service briefings for technicians */
  JOB_BRIEFINGS: 'jobBriefings',

  /** Runtime config for the briefing pipeline (non-technician label list, etc.) */
  BRIEFING_CONFIG: 'briefingConfig',

  /** AI-extracted training data from service history */
  TRAINING_DATA: 'trainingData',

  /** Aggregated equipment profiles for AI analysis */
  EQUIPMENT_PROFILES: 'equipmentProfiles',

  /** AI-analyzed service images with captions and defect detection */
  IMAGE_ANALYSIS: 'imageAnalysis',

  /** Detected recurring problem patterns by equipment type */
  PROBLEM_PATTERNS: 'problemPatterns',

  /** Expert tips contributed by senior technicians */
  EXPERT_TIPS: 'expertTips',

  /** Curation activity log for audit trail */
  CURATION_ACTIVITY: 'curationActivity',

  /** Per-curator gamification stats */
  CURATOR_STATS: 'curatorStats',

  // ─── AI Intelligence Module ────────────────────────────────────────────────

  /** Production AI outputs with feedback tracking */
  AI_OUTPUTS: 'ai_outputs',

  /** User feedback on AI outputs */
  AI_FEEDBACK: 'ai_feedback',

  /** Training prompts for AI systems */
  TRAINING_PROMPTS: 'training_prompts',

  /** Training data campaigns */
  TRAINING_CAMPAIGNS: 'training_campaigns',

  /** AI task assignments for sample review */
  AI_TASKS: 'ai_tasks',

  /** AI query registry with performance metrics */
  AI_QUERY_REGISTRY: 'ai_query_registry',

  /** RAG/Chat query logs */
  RAG_LOGS: 'rag_logs',

  /** Learned failure patterns from Albert feedback analysis — written by scheduled pipeline, read by RAG prompt injection */
  ALBERT_FEEDBACK_PATTERNS: 'albert_feedback_patterns',

  /** Weekly knowledge gap digests — aggregated unanswered agent queries (P20) */
  KNOWLEDGE_GAP_DIGESTS: 'knowledge_gap_digests',

  /** Anomaly findings — nightly statistical outlier detection (P20 Feature 2b) */
  ANOMALY_FINDINGS: 'anomaly_findings',

  /** Learning events from feedback analysis */
  LEARNING_EVENTS: 'learning_events',

  /** Workflow learning configurations */
  WORKFLOW_LEARNING_CONFIGS: 'workflow_learning_configs',

  /** A/B test records */
  AB_TESTS: 'ab_tests',

  /** User AI access control */
  USER_AI_ACCESS: 'user_ai_access',

  // ─── AppSheet Archives ──────────────────────────────────────────────────────
  /** Archived service logs (completed/older tickets) */
  APPSHEET_SERVICE_LOGS_ARCHIVE: 'appsheet_service_logs_archive',

  // ===========================================
  // Smart Document Fill & Company Info
  // ===========================================

  /** Company info settings doc name (under organizations/{orgId}/settings/) */
  COMPANY_INFO_SETTINGS: 'companyInfo',

  /** Company entities subcollection (under organizations/{orgId}/) */
  ENTITIES: 'entities',

  /** Document fill requests — request/approval workflow */
  DOCUMENT_REQUESTS: 'documentRequests',

  /** E-signature requests sent through SignHub (under organizations/{orgId}/). Server-written; the functions-ai twin is signhub/esignRequestRepository.ts ESIGN_REQUESTS_COLLECTION. */
  ESIGN_REQUESTS: 'esignRequests',

  /** Secure audit log for sensitive data access */
  SECURE_AUDIT_LOG: 'secureAuditLog',

  /** Append-only audit trail for sensitive admin actions (impersonation, role changes, etc.) */
  ADMIN_AUDIT_LOG: 'adminAuditLog',

  // ===========================================
  // P9 - HR Module
  // ===========================================

  /** Employee records - candidates through alumni */
  EMPLOYEES: 'employees',

  /** Employee credentials - licenses, certifications */
  EMPLOYEE_CREDENTIALS: 'credentials',

  /** Employee background check records - multi-check history, Drive file links */
  EMPLOYEE_BACKGROUND_CHECKS: 'backgroundChecks',

  /** Employee disciplinary records */
  EMPLOYEE_DISCIPLINARY: 'disciplinaryRecords',

  /** Employee policy notices and acknowledgements */
  EMPLOYEE_NOTICES: 'notices',

  /** HR policy documents */
  POLICIES: 'policies',

  /** Onboarding templates - per-position/department step libraries */
  ONBOARDING_TEMPLATES: 'onboardingTemplates',

  /** HR settings subcollection (under organizations/{orgId}/) */
  HR_SETTINGS: 'hrSettings',

  /** Onboarding checklists - per-employee active onboarding instances */
  ONBOARDING_CHECKLISTS: 'onboardingChecklists',

  /** Background check packages - tier/position configurations */
  BACKGROUND_CHECK_PACKAGES: 'backgroundCheckPackages',

  /** HR audit log - access and action trail */
  HR_AUDIT_LOG: 'hrAuditLog',

  /** Credential types - admin-configurable credential categories */
  CREDENTIAL_TYPES: 'credentialTypes',

  // ==== Geocoding ====

  /** Cached geocoding results (address → lat/lng) */
  GEOCODE_CACHE: 'geocodeCache',

  // ===========================================
  // Agent Framework
  // ===========================================

  /** Agent definitions — seeded configs for autonomous agents */
  AGENT_CONFIGS: 'agent_configs',

  /** Agent execution history — one doc per run */
  AGENT_RUNS: 'agent_runs',

  /** Human-in-the-loop task queue — approval/review items from agent runs */
  AGENT_TASKS: 'agent_tasks',

  // ===========================================
  // Product Analytics
  // ===========================================

  /** Web Vitals performance metrics captured from real users (30-day TTL) */
  PERFORMANCE_METRICS: 'performance_metrics',

  /** Per-integration health check snapshots — written by systemHealth_runChecks every 15 minutes */
  SYSTEM_HEALTH: 'system_health',

  /** One doc per scheduled producer — unconditional run-marker for liveness/staleness guards (server-write only) */
  HEALTH_RUN_MARKERS: 'health_run_markers',

  // ===========================================
  // AI Mailroom
  // ===========================================

  /** Inbound emails received via AI Mailroom — metadata, attachments, classification, processing status */
  EMAIL_INTAKE_QUEUE: 'email_intake_queue',

  /** Registry of email processor rules mapping email types to composite skills */
  EMAIL_PROCESSORS: 'email_processors',

  /** Firestore-defined custom skills for Al — org-scoped */
  AL_SKILLS: 'al_skills',

  // ===========================================
  // Time Tracking (P12 / Manager)
  // ===========================================

  /** Time records — org-scoped, one per employee per day */
  TIME_RECORDS: 'timeRecords',
  /** GPS breadcrumb trail — subcollection of timeRecords */
  TIME_RECORD_GPS_TRAIL: 'gpsTrail',
  /** Audit trail of field-level changes — subcollection of timeRecords */
  TIME_RECORD_CHANGES: 'changes',
  /** PTO and unpaid time off requests — org-scoped */
  TIME_OFF_REQUESTS: 'timeOffRequests',
  /** Paid holiday calendar per year — org-scoped */
  HOLIDAYS: 'holidays',
  /** Jurisdiction rules, pay types, PW rate schedules — org-scoped document */
  PAY_RATE_CONFIG: 'payRateConfig',
  /** Aggregated pay period data for payroll review — org-scoped */
  PAY_PERIOD_SUMMARIES: 'payPeriodSummaries',
  /** QBO TimeActivity export receipts — org-scoped */
  PAYROLL_EXPORTS: 'payrollExports',
  /** Configurable auto-detection flag rules — org-scoped document */
  FLAG_RULES: 'flagRules',
  /** Cached PTO balances from QBO — subcollection of employees */
  PTO_BALANCE_CACHE: 'ptoBalance',

  // ===========================================
  // Marketing Module (P15)
  // ===========================================

  /** Equipment catalog — synced from onpointgen-website MDX files; read-only in OfficeHub */
  EQUIPMENT_CATALOG: 'equipmentCatalog',

  /** Marketing posts — drafts, scheduled, and published content */
  MARKETING_POSTS: 'marketingPosts',
  /** Marketing media assets — images, videos for posts */
  MARKETING_ASSETS: 'marketingAssets',
  /** Marketing campaigns — grouping for related posts */
  MARKETING_CAMPAIGNS: 'marketingCampaigns',
  /** Marketing settings — brand guide, approvers, channel config */
  MARKETING_SETTINGS: 'marketingSettings',
  /** Marketing ideas — AI-generated ideas the user has explicitly kept for later use */
  MARKETING_IDEAS: 'marketingIdeas',
  /** Marketing agent state — subcollection tracking coordinator run state */
  MARKETING_AGENT_STATE: 'marketingAgentState',
  /** GA4 web analytics snapshots — sessions, users, conversions per date×channel×source */
  GA4_METRICS: 'ga4Metrics',
  /** Media inquiries — PR inbox items from media@onpointgen.com */
  MEDIA_INQUIRIES: 'mediaInquiries',
  /** Media scans — trade publication scan results */
  MEDIA_SCANS: 'mediaScans',
  /** Media watchlist — monitored trade publications */
  MEDIA_WATCHLIST: 'mediaWatchlist',

  /** Testimonials — submitted via website form, reviewed and published by marketing team */
  TESTIMONIALS: 'testimonials',

  // ===========================================
  // Parts Pricing Intelligence
  // ===========================================

  /** Per-part per-realm pricing intelligence — cost records, margin analysis, QBO price sync */
  PARTS_PRICING_INTEL: 'partsPricingIntel',

  /** Audit trail subcollection of partsPricingIntel — price change history */
  PRICING_CHANGE_LOG: 'pricingChangeLog',

  /** Inventory units — one record per serialized item, ledger of QBO #130 (Inventory Asset) */
  INVENTORY_UNITS: 'inventoryUnits',

  /** Inventory reconciler per-realm settings — COGS account config, match cutoff date */
  INVENTORY_RECONCILER_SETTINGS: 'inventoryReconcilerSettings',

  /** Inventory match claims — one doc per (realm, invoice, line); enforces
   * single-unit-per-line under concurrent operator matches via a transaction
   * that does txn.create() on a deterministic doc ID. */
  INVENTORY_MATCH_CLAIMS: 'inventoryMatchClaims',

  // ===========================================
  // SEO / GEO (P15 — Marketing Module)
  // ===========================================

  /** Tracked keywords with rank history, cluster grouping, and search intent */
  SEO_KEYWORDS: 'seoKeywords',
  /** Daily GSC search analytics snapshots — clicks, impressions, CTR, position per page×query */
  SEO_METRICS: 'seoMetrics',
  /** Competitor profiles — domain, indexed page count, content velocity */
  COMPETITORS: 'competitors',
  /** Competitor crawled pages — subcollection of competitors/{id}/pages */
  COMPETITOR_PAGES: 'pages',
  /** NAP citation listings across directories — consistency tracking */
  CITATIONS: 'citations',
  /** Canonical Name/Address/Phone — single source of truth for NAP comparisons */
  SEO_CANONICAL_NAP: 'seoCanonicalNAP',
  /** GEO visibility probe results — LLM brand mention tracking per provider×prompt */
  GEO_VISIBILITY: 'geoVisibility',
  /** GEO prompt library — curated prompts for weekly LLM probes */
  GEO_PROMPTS: 'geoPrompts',
  /** Entity authority tracking — Wikidata status, brand mentions, backlink tiers */
  ENTITY_MENTIONS: 'entityMentions',
  /** Google Business Profile snapshot — completeness score, verification, categories */
  GBP_PROFILE: 'gbpProfile',
  /** Google Business Profile reviews with sentiment and extracted keywords */
  GBP_REVIEWS: 'gbpReviews',
  /** Google Business Profile daily insights — views, searches, actions */
  GBP_INSIGHTS: 'gbpInsights',
  /** Keyword gaps — competitors rank but OnPoint doesn't */
  SEO_KEYWORD_GAPS: 'seoKeywordGaps',
  /** AI-ingestible surface health checks — weekly validation of website API endpoints */
  SEO_AI_SURFACES: 'seoAiSurfaces',

  // ===========================================
  // RFP Intake
  // ===========================================

  /** RFP records — deduplicated RFP opportunities with AI fit scoring */
  RFP_RECORDS: 'rfp_records',

  /** Portal adapters — per-portal scraping/parsing configurations for RFP aggregator sites */
  PORTAL_ADAPTERS: 'portal_adapters',

  // ---------------------------------------------------------------------------
  // Recruiting module
  // ---------------------------------------------------------------------------
  RECRUITING_IMPORT_JOBS: 'recruiting_import_jobs',
  RECRUITING_JOBS: 'recruiting_jobs',
  RECRUITING_JOB_TEMPLATES: 'recruiting_job_templates',
  RECRUITING_OPPORTUNITIES: 'recruiting_opportunities',
  RECRUITING_APPLICATIONS: 'recruiting_applications',
  RECRUITING_PIPELINE_EVENTS: 'recruiting_pipeline_events',
  RECRUITING_EVENTS: 'recruiting_events',
  RECRUITING_INTERVIEWS: 'recruiting_interviews',
  RECRUITING_SCORECARDS: 'recruiting_scorecards',
  RECRUITING_PARTNERS: 'recruiting_partners',
  RECRUITING_PARTNER_ACTIVITIES: 'recruiting_partner_activities',
  RECRUITING_TALENT_POOLS: 'recruiting_talent_pools',
  RECRUITING_SEQUENCES: 'recruiting_sequences',
  RECRUITING_SEQUENCE_ENROLLMENTS: 'recruiting_sequence_enrollments',
  RECRUITING_NOTES: 'recruiting_notes',

  // ---------------------------------------------------------------------------
  // Huddle module (P19) — operating rhythm: meetings, action items, decisions,
  // announcements, VTO, accountability summary. See docs/DESIGN-Huddle.md §4.
  //
  // HR Seats — accountability chart / org chart. Owned by HR (P9).
  // Huddle reads hr_seats read-only for in-meeting accountability summary.
  HR_SEATS: 'hr_seats' as const,
  /** Role tag vocabulary — org-scoped; slug is the key modules read. */
  ROLE_TAGS: 'roleTags' as const,
  // HUDDLE_ROCKS is reserved but Rocks UI is deferred from v1 per 5/11 alignment
  // (DESIGN §4.4 preserves the schema). The constant ships now so the rule block,
  // type, and dictionary entry can ship together.
  // ---------------------------------------------------------------------------
  HUDDLE_MEETING_TEMPLATES: 'huddle_meeting_templates',
  HUDDLE_MEETING_INSTANCES: 'huddle_meeting_instances',
  HUDDLE_ACTION_ITEMS: 'huddle_action_items',
  HUDDLE_ROCKS: 'huddle_rocks',
  HUDDLE_DECISIONS: 'huddle_decisions',
  HUDDLE_ANNOUNCEMENTS: 'huddle_announcements',
  HUDDLE_ANNOUNCEMENT_DELIVERIES: 'huddle_announcement_deliveries',
  HUDDLE_VTO: 'huddle_vto',
  HUDDLE_ACTIVITY_FEED: 'huddle_activity_feed',

  // ===========================================
  // Data Health
  // ===========================================

  /** Dictionary stats — daily record counts and field coverage per collection */
  DICTIONARY_STATS: 'dictionary_stats',

  /** Collection health — drift, quality grades, provenance, triage status per collection */
  COLLECTION_HEALTH: 'collection_health',

  // ===========================================
  // Safety (P17)
  // ===========================================

  /** Safety documents — manuals, procedures, policies, reports, and form references */
  SAFETY_DOCUMENTS: 'safetyDocuments',

  /** Safety Data Sheets (SDS/MSDS) — one record per chemical product, latest PDF + supersede history */
  SAFETY_SDS_SHEETS: 'safetySdsSheets',

  /** Insurance records — workers comp, auto, and general liability per entity */
  SAFETY_INSURANCE: 'safetyInsurance',

  /** Certificates of Insurance from vendors/subcontractors and to customers — tracked for expiration and compliance by david-ai */
  COI_CERTIFICATES: 'coiCertificates',

  /** Customer COI requirements — coverages/limits a customer or project requires from us, tracked vs. fulfillment by david-ai */
  COI_REQUIREMENTS: 'coiRequirements',

  /** Insurance policies — source of truth for own coverage lines (replaces safetyInsurance) */
  INSURANCE_POLICIES: 'insurancePolicies',

  /** Insurance claims — filed against a specific policy */
  INSURANCE_CLAIMS: 'insuranceClaims',

  /** Carrier/broker portal registry — wayfinding only (URLs, what's there, contacts; no credentials) */
  INSURANCE_PORTALS: 'insurancePortals',

  /** Shop jobs (docs/shop/PRD.md §8) — server-written projection, org + location scoped */
  SHOP_CASES: 'shopCases',

  /** Subcollection of shopCases — append-only event log ordered by seq */
  SHOP_CASE_EVENTS: 'events',

  /** Shop locations and their settings (PRD §8.9) */
  SHOP_LOCATIONS: 'shopLocations',

  /** Organization-default Shop settings, id = organizationId (PRD §8.9) */
  SHOP_SETTINGS: 'shopSettings',

  /**
   * Idempotency records for Shop mutations, id = {organizationId}_{uid}_{clientMutationId}.
   * The {uid} segment is NOT optional and was missing from this comment before #4941: the key is
   * per-CALLER, so two users picking the same clientMutationId get two separate records instead of
   * one silently reading back the other's result (PR C security review, 2026-09-17). A shared
   * keyspace across every idempotent Shop action (createCase, moveStage) -- each action folds its
   * own name into the payload hash, so one id reused across two actions can never hash-match.
   */
  SHOP_MUTATIONS: 'shopMutations',

  /** Case-number counters, id = {organizationId}_{locationId}_{yymmdd} */
  SHOP_COUNTERS: 'shopCounters',

  /** Subcollection of shopLocations and shopSettings — server-only change history */
  SHOP_HISTORY: 'history',

  /** Policy document library — declarations, binders, endorsements, audits with Gemini-extracted structured coverage (all commercial lines) */
  INSURANCE_POLICY_DOCUMENTS: 'policyDocuments',

  /** Reconciliation audit findings — discrepancies between policy documents and policy records */
  POLICY_AUDITS: 'policyAudits',

  /** Incident reports — workplace incidents, near misses, OSHA recordable events */
  SAFETY_INCIDENTS: 'safetyIncidents',

  /** Job Hazard Analyses — pre-job safety planning with SIF screening, risk matrix, crew sign-off */
  SAFETY_JHA: 'safetyJHA',

  /** Crew sign-off subcollection under safetyJHA/{id}/crewAcknowledgments */
  JHA_CREW_ACKNOWLEDGMENTS: 'crewAcknowledgments',
  /** MOC update records subcollection under safetyJHA/{id}/updates */
  JHA_UPDATES: 'updates',

  /** Safety meeting records — structured meeting log with attendees, topics, action items */
  SAFETY_MEETINGS: 'safetyMeetings',

  /** Attachments subcollection under safetyMeetings/{id}/attachments */
  MEETING_ATTACHMENTS: 'attachments',

  /** SnackPulse training completion data synced nightly for safety training compliance */
  SAFETY_TRAINING_SYNC: 'safetyTrainingSync',

  // Command Center (P6) — consumed read-only by Safety for OSHA TRIR/DART
  /** Monthly labor hours by entity and category — seeded from payroll data */
  LABOR_HOURS_MONTHLY: 'labor_hours_monthly',

  // ===========================================
  // Investor Relations (P18)
  //


  /** Investor records — individual and entity investors */
  INVESTORS: 'investors',

  /** Share class definitions — preferred, common, etc. */
  SHARE_CLASSES: 'shareClasses',

  /** Investor holdings — share ownership records */
  HOLDINGS: 'holdings',

  /** Investor documents — agreements, certificates, tax docs, reports */
  INVESTOR_DOCUMENTS: 'investorDocuments',

  /** Investor updates — quarterly reports, memos, announcements */
  INVESTOR_UPDATES: 'investorUpdates',

  /** Consent requests — board consent items requiring investor approval */
  CONSENT_REQUESTS: 'consentRequests',

  /** Investor audit log — access and action trail for IR module */
  INVESTOR_AUDIT_LOG: 'investorAuditLog',

  /** Consent votes — subcollection under consentRequests/{consentId}/consentVotes */
  CONSENT_VOTES: 'consentVotes',
  // Workshop (P19) — Per-user productivity utilities
  // ===========================================

  /** Workshop tool grants — per-user-per-tool explicit grants */
  WORKSHOP_TOOL_GRANTS: 'workshopToolGrants',

  /** Workshop tool org-level config overrides (force-on / force-off per tool per org) */
  WORKSHOP_TOOL_ORG_CONFIGS: 'workshopToolOrgConfigs',

  /** Workshop grant audit log — append-only history of grant/revoke/override actions */
  WORKSHOP_GRANT_AUDIT: 'workshopGrantAudit',

  /** Outreach Letters — per-user sender profiles (letterhead, signature, return address) */
  OUTREACH_SENDER_PROFILES: 'outreachSenderProfiles',

  /** Outreach Letters — recipient contacts with researched addresses */
  OUTREACH_CONTACTS: 'outreachContacts',

  /** Outreach Letters — draft and dispatched letters */
  OUTREACH_LETTERS: 'outreachLetters',

  /** Outreach Letters — AI research sessions with grounded sources and confidence */
  OUTREACH_RESEARCH_SESSIONS: 'outreachResearchSessions',

  // Agent Catch-Up Queue
  // ===========================================

  /** Catch-up queue items — pending work product from agents needing human attention */
  CATCHUP_QUEUE: 'catchup_queue',

  /** Catch-up sessions — tracks in-progress and completed catch-up sessions */
  CATCHUP_SESSIONS: 'catchup_sessions',

  // ===========================================
  // Profitability
  // ===========================================

  /** Precomputed per-job profitability documents */
  JOB_PROFITABILITY: 'jobProfitability',

  /** Technician burdened hourly cost rates */
  TECHNICIAN_COST_RATES: 'technicianCostRates',

  /** Default hourly rates by labor category (service/install/shop) */
  LABOR_CATEGORY_DEFAULTS: 'laborCategoryDefaults',

  // ===========================================
  // Kohler Integration
  // ===========================================

  /** Kohler portal scraper session tokens and state */
  KOHLER_SESSIONS: 'kohler_sessions',

  // ===========================================
  // System Flags (platform-internal, Admin SDK writes only)
  // ===========================================

  /** Top-level collection for platform system control flags — Admin SDK writes only, admin/developer read */
  SYSTEM_FLAGS: '_system_flags',

  /** Doc ID: Gemini circuit breaker state — tripped/trippedAt/reason */
  GEMINI_CIRCUIT_BREAKER_DOC: 'gemini_circuit_breaker',

  /** Doc ID: Gemini hourly + daily request counters with callerBreakdown */
  GEMINI_REQUEST_COUNTER_DOC: 'gemini_request_counter',

  // ===========================================
  // Launcher Dashboard
  // ===========================================

  /** Announcements feed items (subcollection under organizations) */
  ANNOUNCEMENTS: 'announcements',
} as const;

/**
 * Type representing valid collection names.
 * Use this type when you need to accept any collection name as a parameter.
 */
export type CollectionName = (typeof COLLECTIONS)[keyof typeof COLLECTIONS];

